import { Draft } from '@reduxjs/toolkit'
import { OrderSummaryOrderItem, TicketItemStatus } from '@ancon/wildcat-types'

import { CheckOrderTicketEntityTicketItem, OrderReducerState } from '../types'

function updateOrderItemTicketItemsStatus(
  orderItem: Draft<OrderSummaryOrderItem>,
  status: TicketItemStatus,
) {
  const subProducts = orderItem.items ?? []
  const isMultiProductOrderItem = subProducts.length > 0

  // Update ticket items status of the order items
  orderItem.ticketItems.forEach(ticketItem => {
    if (ticketItem.status < status) {
      // eslint-disable-next-line no-param-reassign
      ticketItem.status = status
    }
  })

  // Update sub products ticket items, if multi-product
  if (isMultiProductOrderItem) {
    subProducts.forEach(subProduct => {
      subProduct.ticketItems.forEach(ticketItem => {
        if (ticketItem.status < status) {
          // eslint-disable-next-line no-param-reassign
          ticketItem.status = status
        }
      })
    })
  }
}

/**
 * This can be used to update the status of ticket items in a specific ticket.
 */
export default function updateTicketItemsStatus(
  state: Draft<OrderReducerState>,
  ticketId: string,
  ticketItemIds: string[],
  ticketItemStatus: TicketItemStatus,
) {
  const ticketEntity = state.checkOrderSummaryTickets.entities[ticketId]

  const orderSummary = state.checkOrderSummary

  // Order item IDs of the ticket items to be updated in STEP 2
  const orderItemIds: string[] = []

  function updateTicketItemStatus<
    T extends {
      id: string
      status: TicketItemStatus
    },
  >(ticketItem: Draft<T>) {
    const { id, status } = ticketItem

    if (ticketItemIds.includes(id) && ticketItemStatus > status) {
      // eslint-disable-next-line no-param-reassign
      ticketItem.status = ticketItemStatus
      return true
    }

    return false
  }

  // STEP 1: Update ticket items in ticket entity
  if (ticketEntity) {
    ticketEntity.ticketItems = ticketEntity.ticketItems.reduce<
      CheckOrderTicketEntityTicketItem[]
    >((acc, ticketItem) => {
      const { orderItemId, ticketItems } = ticketItem

      let updated = false

      const subProductsTicketItems = ticketItems ?? []
      const isMultiProductTicketItem = subProductsTicketItems.length > 0

      // Update parent ticket item status
      if (updateTicketItemStatus(ticketItem)) {
        updated = true
      }

      // Update sub products ticket items, if multi-product
      if (isMultiProductTicketItem) {
        subProductsTicketItems.forEach(subProductTicketItem => {
          if (updateTicketItemStatus(subProductTicketItem)) {
            updated = true
          }
        })
      }

      // If ticket item updated, store order it's order item ID to update later in STEP 2
      if (updated && !orderItemIds.includes(orderItemId)) {
        orderItemIds.push(orderItemId)
      }

      acc.push(ticketItem)

      return acc
    }, [])
  }

  // STEP 2: Update ticket items in order items of summary
  if (orderSummary) {
    orderSummary.items = orderSummary.items.reduce<OrderSummaryOrderItem[]>(
      (acc, orderItem) => {
        // If this order item has ticket items to be updated
        if (orderItemIds.includes(orderItem.id)) {
          updateOrderItemTicketItemsStatus(orderItem, ticketItemStatus)
        }
        acc.push(orderItem)

        return acc
      },
      [],
    )
  }

  return ticketEntity
}
